import React, { useCallback, useEffect, useState, Children, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Instagram, ChevronLeftIcon, ChevronRightIcon, MapPin, Clock, CheckCircle2, XCircle, Sparkles, Gem, Crown, Heart, ChevronDown, Star, Menu, X, MessageCircle, User } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const galleryImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/pesBiUsAYpb1Hze9RXUMZf/1.png',
  alt: 'French tip con diseño de copos de nieve',
  label: 'Diseño Invernal'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9ZGRwGYV4Bf6phimHxvsCY/2.png',
  alt: 'French tip azul con ondas de mármol',
  label: 'Mármol Azul'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oXyE2qSXEh7w4EbgRpXQ2i/3.png',
  alt: 'Diseño invernal con textura de tejido',
  label: 'Textura Tejido'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/a6NQpG99KaTKpFuFgGagMJ/4.png',
  alt: 'Diseño coral con ondas doradas',
  label: 'Coral & Oro'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kiwnhQ15T52TE5FXDTUyFo/5.png',
  alt: 'Diseño animal print con Minnie Mouse',
  label: 'Animal Print'
}];
function GalleryCarousel() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % galleryImages.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, []);
  // Auto-advance every 4 seconds
  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };
  // Get indices for side previews
  const prevIdx = (current - 1 + galleryImages.length) % galleryImages.length;
  const nextIdx = (current + 1) % galleryImages.length;
  return <div className="relative" data-id="element-0">
      {/* Main carousel area */}
      <div className="flex items-center justify-center gap-4 md:gap-6" data-id="element-1">
        {/* Left preview — desktop only */}
        <div className="hidden md:block w-48 lg:w-56 aspect-square rounded-sm overflow-hidden opacity-30 cursor-pointer shrink-0 transition-opacity hover:opacity-50" onClick={prev} data-id="element-2">
          <img src={galleryImages[prevIdx].src} alt={galleryImages[prevIdx].alt} className="w-full h-full object-cover" data-id="element-3" />
        </div>

        {/* Center — main image */}
        <div className="relative w-full max-w-md md:max-w-lg aspect-square overflow-hidden rounded-sm border border-white/10" data-id="element-4">
          <AnimatePresence mode="wait" custom={direction} data-id="element-5">
            <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0" data-id="element-6">
              <img src={galleryImages[current].src} alt={galleryImages[current].alt} className="w-full h-full object-cover" data-id="element-7" />
              {/* Bottom label */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 pt-12" data-id="element-8">
                <p className="text-white font-serif text-lg" data-id="element-9">
                  {galleryImages[current].label}
                </p>
                <p className="text-gray-300 text-sm font-light mt-1" data-id="element-10">
                  @paotaborda_nails
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Gold decorative frame */}
          <div className="absolute inset-0 border border-luxury-gold/10 rounded-sm pointer-events-none" data-id="element-11"></div>
        </div>

        {/* Right preview — desktop only */}
        <div className="hidden md:block w-48 lg:w-56 aspect-square rounded-sm overflow-hidden opacity-30 cursor-pointer shrink-0 transition-opacity hover:opacity-50" onClick={next} data-id="element-12">
          <img src={galleryImages[nextIdx].src} alt={galleryImages[nextIdx].alt} className="w-full h-full object-cover" data-id="element-13" />
        </div>
      </div>

      {/* Navigation arrows */}
      <button onClick={prev} className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/60 backdrop-blur-sm border border-white/10 rounded-full text-white hover:border-luxury-gold/50 hover:text-luxury-gold transition-all" aria-label="Imagen anterior" data-id="element-14">
        <ChevronLeftIcon className="w-5 h-5" data-id="element-15" />
      </button>
      <button onClick={next} className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/60 backdrop-blur-sm border border-white/10 rounded-full text-white hover:border-luxury-gold/50 hover:text-luxury-gold transition-all" aria-label="Siguiente imagen" data-id="element-16">
        <ChevronRightIcon className="w-5 h-5" data-id="element-17" />
      </button>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2.5 mt-8" data-id="element-18">
        {galleryImages.map((_, idx) => <button key={idx} onClick={() => {
        setDirection(idx > current ? 1 : -1);
        setCurrent(idx);
      }} className={`transition-all duration-300 rounded-full ${idx === current ? 'w-8 h-2 bg-luxury-gold' : 'w-2 h-2 bg-white/20 hover:bg-white/40'}`} aria-label={`Ver imagen ${idx + 1}`} data-id="element-19" />)}
      </div>
    </div>;
}
const socialProofData = [{
  name: 'María González',
  area: 'Corona',
  service: 'Acrílicas'
}, {
  name: 'Daniela Reyes',
  area: 'Astoria',
  service: 'Esculpidas'
}, {
  name: 'Camila Torres',
  area: 'Queens',
  service: 'Gel Premium'
}, {
  name: 'Valentina Cruz',
  area: 'Jackson Heights',
  service: 'Dip Powder'
}, {
  name: 'Isabela Moreno',
  area: 'Flushing',
  service: 'Acrílicas'
}, {
  name: 'Sofía Ramírez',
  area: 'Elmhurst',
  service: 'Esculpidas'
}, {
  name: 'Natalia Flores',
  area: 'Woodside',
  service: 'Gel Premium'
}, {
  name: 'Andrea López',
  area: 'Bronx',
  service: 'Acrílicas'
}, {
  name: 'Paola Jiménez',
  area: 'Manhattan',
  service: 'Dip Powder'
}, {
  name: 'Gabriela Vargas',
  area: 'Brooklyn',
  service: 'Esculpidas'
}];
function getRandomMinutes() {
  return Math.floor(Math.random() * 27) + 2;
}
export function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [notificationIndex, setNotificationIndex] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  const [randomMinutes, setRandomMinutes] = useState(getRandomMinutes);
  const [isScrolling, setIsScrolling] = useState(false);
  // Scroll detection — pause notifications while scrolling
  useEffect(() => {
    let scrollTimer: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => setIsScrolling(false), 2000);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
    };
  }, []);
  // Notification rotation
  useEffect(() => {
    if (notificationDismissed) return;
    const initialDelay = setTimeout(() => {
      if (!isScrolling) setShowNotification(true);
    }, 3000);
    const interval = setInterval(() => {
      if (isScrolling) return;
      setShowNotification(false);
      setTimeout(() => {
        setNotificationIndex(prev => (prev + 1) % socialProofData.length);
        setRandomMinutes(getRandomMinutes());
        setShowNotification(true);
      }, 3000);
    }, 7000); // 4s visible + 3s gap
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [notificationDismissed, isScrolling]);
  const dismissNotification = () => {
    setShowNotification(false);
    setNotificationDismissed(true);
    // Resume after 5 seconds
    setTimeout(() => {
      setNotificationDismissed(false);
      setNotificationIndex(prev => (prev + 1) % socialProofData.length);
      setRandomMinutes(getRandomMinutes());
      setShowNotification(true);
    }, 5000);
  };
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  return <div className="min-h-screen bg-luxury-black text-gray-200 selection:bg-luxury-emerald selection:text-white" data-id="element-20">
      {/* HEADER */}
      <header className="fixed top-0 w-full z-50 bg-luxury-black/90 backdrop-blur-md border-b border-white/5" data-id="element-21">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between" data-id="element-22">
          <div className="flex-shrink-0 z-50" data-id="element-23">
            <img src="https://cdn.magicpatterns.com/uploads/reWmgqgz4SXDRvm7SyGVzz/logoPweb1.png" alt="Paola Borda Nails Logo" className="h-12 w-auto object-contain" data-id="element-24" />
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6" data-id="element-25">
            <nav className="flex items-center gap-6 mr-4 text-sm font-light" data-id="element-26">
              <a href="#historia" className="hover:text-luxury-gold transition-colors" data-id="element-27">
                Historia
              </a>
              <a href="#servicios" className="hover:text-luxury-gold transition-colors" data-id="element-28">
                Servicios
              </a>
              <a href="#galeria" className="hover:text-luxury-gold transition-colors" data-id="element-29">
                Galería
              </a>
            </nav>
            <a href="tel:347-842-7103" className="flex items-center gap-2 text-sm font-medium hover:text-luxury-gold transition-colors" data-id="element-30">
              <Phone size={16} data-id="element-31" />
              <span data-id="element-32">347-842-7103</span>
            </a>
            <a href="https://instagram.com/paotaborda_nails" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-luxury-gold transition-colors" data-id="element-33">
              <Instagram size={20} data-id="element-34" />
            </a>
            <a href="#reserva" className="bg-luxury-emerald hover:bg-luxury-emeraldDark text-white px-5 py-2.5 rounded-sm text-sm font-medium transition-all duration-300 shadow-[0_0_15px_rgba(12,107,58,0.3)]" data-id="element-35">
              Reservar
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-4 z-50" data-id="element-36">
            <a href="#reserva" onClick={closeMenu} className="bg-luxury-emerald text-white px-4 py-2 rounded-sm text-xs font-medium" data-id="element-37">
              Reservar
            </a>
            <button onClick={toggleMenu} className="text-white p-2" data-id="element-38">
              {isMenuOpen ? <X size={24} data-id="element-39" /> : <Menu size={24} data-id="element-40" />}
            </button>
          </div>
        </div>

        {/* Mobile Accordion Menu */}
        <AnimatePresence data-id="element-41">
          {isMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="md:hidden bg-luxury-dark border-b border-white/10 overflow-hidden" data-id="element-42">
              <nav className="flex flex-col px-6 py-4 gap-4 text-lg font-light" data-id="element-43">
                <a href="#historia" onClick={closeMenu} className="py-2 border-b border-white/5 hover:text-luxury-gold" data-id="element-44">
                  Historia
                </a>
                <a href="#servicios" onClick={closeMenu} className="py-2 border-b border-white/5 hover:text-luxury-gold" data-id="element-45">
                  Servicios
                </a>
                <a href="#galeria" onClick={closeMenu} className="py-2 border-b border-white/5 hover:text-luxury-gold" data-id="element-46">
                  Galería
                </a>
                <a href="#reserva" onClick={closeMenu} className="py-2 hover:text-luxury-gold" data-id="element-47">
                  Contacto & Reserva
                </a>
                <div className="flex items-center gap-4 pt-4 mt-2 border-t border-white/10" data-id="element-48">
                  <a href="tel:347-842-7103" className="flex items-center gap-2 text-luxury-gold" data-id="element-49">
                    <Phone size={18} data-id="element-50" />
                    <span data-id="element-51">347-842-7103</span>
                  </a>
                  <a href="https://instagram.com/paotaborda_nails" target="_blank" rel="noopener noreferrer" className="text-gray-300" data-id="element-52">
                    <Instagram size={22} data-id="element-53" />
                  </a>
                </div>
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* HERO SECTION — SPLIT SCREEN */}
      <section className="relative flex flex-col md:flex-row md:h-screen overflow-hidden" data-id="element-54">
        {/* LEFT SIDE — Text Content */}
        <div className="relative z-10 w-full md:w-[50%] bg-[#0D0D0D] flex items-center order-2 md:order-1" data-id="element-55">
          <div className="w-full max-w-2xl mx-auto px-6 sm:px-10 lg:px-16 py-12 md:py-0 md:ml-auto md:mr-0 lg:pr-20 pt-24 md:pt-0" data-id="element-56">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-57">
              <motion.div variants={fadeIn} className="mb-8" data-id="element-58">
                <Crown className="text-luxury-gold w-7 h-7" data-id="element-59" />
              </motion.div>

              <motion.h1 variants={fadeIn} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-[1.15] mb-6 text-white text-left" data-id="element-60">
                Por Fin — Uñas Que{' '}
                <span className="text-gradient-gold italic font-medium" data-id="element-61">
                  Realmente Lucen
                </span>{' '}
                Como La Foto Que Mostraste.
              </motion.h1>

              <motion.p variants={fadeIn} className="text-base md:text-lg text-gray-400 mb-10 font-light leading-relaxed max-w-lg" data-id="element-62">
                La técnica colombiana de esculpido de uñas que está conquistando
                Corona, NY.
              </motion.p>

              <motion.div variants={fadeIn} className="mb-6" data-id="element-63">
                <a href="#reserva" className="inline-flex items-center justify-center gap-2 bg-luxury-emeraldDark hover:bg-luxury-emerald text-white px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 border border-luxury-gold/40 hover:border-luxury-gold/70 shadow-[0_0_20px_rgba(12,107,58,0.3)] hover:shadow-[0_0_30px_rgba(12,107,58,0.5)] transform hover:-translate-y-0.5" data-id="element-64">
                  Reserva Tu Cita Ahora
                  <Sparkles size={18} data-id="element-65" />
                </a>
              </motion.div>

              <motion.div variants={fadeIn} className="flex flex-col gap-3" data-id="element-66">
                <div className="inline-flex items-center gap-2 bg-luxury-emerald/10 border border-luxury-emerald/30 rounded-full px-4 py-1.5 w-fit" data-id="element-67">
                  <span className="text-sm" data-id="element-68">🔥</span>
                  <span className="text-xs md:text-sm text-luxury-emerald font-medium" data-id="element-69">
                    47 personas reservaron esta semana
                  </span>
                </div>

                <div className="flex items-center gap-1.5 mt-2" data-id="element-70">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-luxury-gold fill-luxury-gold" data-id="element-71" />)}
                  <span className="text-sm text-gray-400 ml-2 font-light" data-id="element-72">
                    Más de 200 clientas satisfechas en Corona, NY
                  </span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* RIGHT SIDE — Image */}
        <div className="relative w-full md:w-[50%] h-[55vh] md:h-full order-1 md:order-2 overflow-hidden" data-id="element-73">
          <img src="https://cdn.magicpatterns.com/uploads/gYJ6H2GVbwZgQwj7idXroz/banner3.png" alt="Paola Borda — Nail Artist" loading="eager" className="w-full h-full object-cover object-top" data-id="element-74" />
          {/* Golden gradient blend on left edge — desktop only */}
          <div className="hidden md:block absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/50 to-transparent" data-id="element-75"></div>
          {/* Bottom fade for mobile — subtle blend into text section */}
          <div className="md:hidden absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#0D0D0D] to-transparent" data-id="element-76"></div>
          {/* Top padding for mobile nav */}
          <div className="md:hidden absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-[#0D0D0D]/80 to-transparent" data-id="element-77"></div>
        </div>
      </section>

      {/* OPENING HOOK - STORYTELLING */}
      <section id="historia" className="py-12 md:py-24 bg-luxury-black relative" data-id="element-78">
        <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-79">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="space-y-8 text-lg md:text-xl text-gray-300 font-light leading-relaxed" data-id="element-80">
            <motion.p variants={fadeIn} data-id="element-81">
              <strong className="text-white font-medium" data-id="element-82">
                Guardaste la foto.
              </strong>
            </motion.p>
            <motion.p variants={fadeIn} data-id="element-83">
              Se la mostraste a la técnica. Fuiste específica. Dijiste{' '}
              <em className="text-gray-100" data-id="element-84">"exactamente así"</em> — la forma,
              el largo, el acabado.
            </motion.p>
            <motion.p variants={fadeIn} data-id="element-85">
              Y luego miraste hacia abajo... y sentiste esa decepción silenciosa
              de nuevo.
            </motion.p>
            <motion.p variants={fadeIn} data-id="element-86">
              Se apuraron. La forma no quedó del todo bien. El color estaba un
              poco diferente. ¿Y en una semana? Levantadas.
            </motion.p>
            <motion.p variants={fadeIn} data-id="element-87">
              Te fuiste a casa diciéndote{' '}
              <em className="text-gray-100" data-id="element-88">"al menos están bien."</em> Pero
              "bien" no era lo que querías. Querías{' '}
              <strong className="text-luxury-gold font-medium" data-id="element-89">
                impresionantes.
              </strong>
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* THE TRUTH / PROBLEM */}
      <section className="py-12 md:py-24 bg-luxury-dark border-y border-white/5" data-id="element-90">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-91">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-92">
            <h2 className="text-3xl md:text-5xl font-serif mb-12 text-white" data-id="element-93">
              Aquí está la verdad que nadie en la industria de belleza quiere
              decir en voz alta:
            </h2>

            <div className="bg-luxury-black p-8 md:p-12 rounded-sm border border-white/10 relative overflow-hidden" data-id="element-94">
              <div className="absolute top-0 left-0 w-1 h-full bg-luxury-emerald" data-id="element-95"></div>
              <p className="text-xl md:text-2xl text-gray-200 font-medium mb-6" data-id="element-96">
                La mayoría de los salones de uñas no están manejados por
                artistas.
              </p>
              <p className="text-gray-400 text-lg mb-6" data-id="element-97">
                Están manejados por personas que intentan atender a la mayor
                cantidad de clientes posible, lo más rápido posible. Tu tiempo
                en la silla es una transacción para ellas.
              </p>
              <p className="text-gray-400 text-lg" data-id="element-98">
                Lo puedes sentir. El apuro. La conversación mínima. La energía
                de "escoge del catálogo."
              </p>
            </div>

            <p className="mt-12 text-2xl font-serif text-gradient-gold italic" data-id="element-99">
              Tú no eres una transacción. Y tus uñas no deberían verse como una.
            </p>
          </motion.div>
        </div>
      </section>

      {/* THE SOLUTION / MECHANISM */}
      <section className="py-12 md:py-24 bg-luxury-black relative overflow-hidden" data-id="element-100">
        {/* Decorative background elements */}
        <div className="absolute top-1/4 -left-64 w-96 h-96 bg-luxury-emerald/10 rounded-full blur-[100px]" data-id="element-101"></div>
        <div className="absolute bottom-1/4 -right-64 w-96 h-96 bg-luxury-gold/5 rounded-full blur-[100px]" data-id="element-102"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-103">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-104">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-105">
              <motion.h2 variants={fadeIn} className="text-3xl md:text-5xl font-serif mb-6 text-white" data-id="element-106">
                ¿Qué pasa cuando una verdadera artista trabaja en tus manos?
              </motion.h2>
              <motion.p variants={fadeIn} className="text-2xl text-luxury-gold font-serif italic mb-8" data-id="element-107">
                Todo cambia.
              </motion.p>
              <motion.div variants={fadeIn} className="space-y-6 text-gray-300 text-lg font-light" data-id="element-108">
                <p data-id="element-109">
                  Ella mira la forma de tu uña. Piensa en tu estilo. Te escucha
                  — realmente te escucha — sobre lo que quieres. Se enorgullece
                  de cada uña como si fuera a ser fotografiada. Porque,
                  honestamente, ¿lo será?
                </p>
                <p className="text-xl text-white font-medium mt-8" data-id="element-110">
                  Esa artista es Paola Borda.
                </p>
                <div className="h-px w-24 bg-gradient-to-r from-luxury-gold to-transparent my-8" data-id="element-111"></div>
                <h3 className="text-2xl font-serif text-white mb-4" data-id="element-112">
                  El Arte de Uñas Colombiano Es Diferente. Aquí Está Por Qué.
                </h3>
                <p data-id="element-113">
                  Paola se formó en la tradición colombiana de nail art — una
                  cultura donde el esculpido de uñas es tratado como un
                  verdadero oficio. Donde la forma, la simetría y la atención al
                  detalle no son extras opcionales. Son el estándar.
                </p>
                <p data-id="element-114">
                  Ella trajo ese estándar a Corona, Nueva York. Y ahora mujeres
                  de todo Queens están pasando de largo su salón del barrio para
                  sentarse en su silla.
                </p>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-115">
              <div className="aspect-[4/5] rounded-sm overflow-hidden border border-white/10 relative" data-id="element-116">
                <img src="https://cdn.magicpatterns.com/uploads/gYJ6H2GVbwZgQwj7idXroz/banner3.png" alt="Paola Borda Nail Art" className="w-full h-full object-cover object-left" data-id="element-117" />
                <div className="absolute inset-0 bg-gradient-to-t from-luxury-black via-transparent to-transparent" data-id="element-118"></div>
                <div className="absolute bottom-8 left-8 right-8" data-id="element-119">
                  <p className="text-xl font-serif italic text-white text-center" data-id="element-120">
                    "Porque una vez que experimentas lo que se siente el
                    verdadero arte de uñas... No regresas atrás."
                  </p>
                </div>
              </div>
              {/* Decorative frame */}
              <div className="absolute -inset-4 border border-luxury-gold/20 -z-10 rounded-sm" data-id="element-121"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO GALLERY */}
      <section id="galeria" className="pt-6 pb-12 md:pt-12 md:pb-24 bg-luxury-black relative overflow-hidden" data-id="element-122">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16 bg-gradient-to-b from-transparent via-luxury-gold/30 to-transparent" data-id="element-123"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-124">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-125">
            <Sparkles className="w-6 h-6 text-luxury-gold mx-auto mb-4" data-id="element-126" />
            <h2 className="text-3xl md:text-5xl font-serif mb-4 text-white" data-id="element-127">
              Su Arte Habla Por Sí Solo
            </h2>
            <p className="text-gray-400 font-light text-lg max-w-xl mx-auto" data-id="element-128">
              Cada set es una pieza única — esculpida a mano con precisión
              colombiana.
            </p>
          </motion.div>

          {/* Dynamic Carousel */}
          <GalleryCarousel data-id="element-129" />

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center mt-12" data-id="element-130">
            <a href="https://instagram.com/paotaborda_nails" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-luxury-gold hover:text-luxury-goldLight transition-colors font-medium" data-id="element-131">
              <Instagram className="w-5 h-5" data-id="element-132" />
              Ver más en @paotaborda_nails
            </a>
          </motion.div>
        </div>
      </section>

      {/* SERVICES TABLE */}
      <section id="servicios" className="py-12 md:py-24 bg-luxury-dark border-y border-white/5" data-id="element-133">
        <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-134">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-135">
            <h2 className="text-3xl md:text-5xl font-serif mb-4 text-white" data-id="element-136">
              Lo Que Paola Ofrece
            </h2>
            <div className="h-1 w-20 bg-luxury-emerald mx-auto" data-id="element-137"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-138">
            {[{
            title: 'Uñas Acrílicas',
            desc: 'Fuertes, duraderas, perfectamente formadas para tus manos',
            icon: <Gem className="text-luxury-gold w-6 h-6" data-id="element-139" />
          }, {
            title: 'Uñas Esculpidas',
            desc: 'Construidas desde cero — forma personalizada, cero compromisos',
            icon: <Sparkles className="text-luxury-gold w-6 h-6" data-id="element-140" />
          }, {
            title: 'Gel Polish',
            desc: 'Color brillante, resistente a las roturas, vibrante y duradero',
            icon: <Heart className="text-luxury-gold w-6 h-6" data-id="element-141" />
          }, {
            title: 'Dip Powder',
            desc: 'Liviano, resistente, perfección de apariencia natural',
            icon: <Crown className="text-luxury-gold w-6 h-6" data-id="element-142" />
          }].map((service, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-luxury-black p-8 rounded-sm border border-white/5 hover:border-luxury-emerald/30 transition-colors group" data-id="element-143">
                <div className="flex items-start gap-4" data-id="element-144">
                  <div className="p-3 bg-white/5 rounded-full group-hover:bg-luxury-emerald/10 transition-colors" data-id="element-145">
                    {service.icon}
                  </div>
                  <div data-id="element-146">
                    <h3 className="text-xl font-medium text-white mb-2" data-id="element-147">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 font-light" data-id="element-148">{service.desc}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="mt-16 text-center text-gray-300 font-light text-lg" data-id="element-149">
            <p data-id="element-150">
              Todos los servicios. Una sola artista. Una sola ubicación. Seis
              días a la semana.
            </p>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR / NOT FOR */}
      <section className="py-12 md:py-24 bg-luxury-black" data-id="element-151">
        <div className="max-w-6xl mx-auto px-4 sm:px-6" data-id="element-152">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-153">
            {/* For You */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="bg-gradient-to-b from-luxury-dark to-luxury-black p-10 rounded-sm border border-luxury-emerald/20" data-id="element-154">
              <h3 className="text-2xl font-serif text-white mb-8 flex items-center gap-3" data-id="element-155">
                <CheckCircle2 className="text-luxury-emerald" data-id="element-156" />
                Esto Es Para Ti Si...
              </h3>
              <ul className="space-y-6" data-id="element-157">
                {['Estás cansada de salir del salón decepcionada', 'Trabajas duro y mereces uñas que reflejen eso', 'Te han fallado técnicas de uñas apresuradas y descuidadas antes', 'Quieres una artista de uñas que realmente se preocupe por el resultado', 'Quieres que tus uñas luzcan como las que ves en Instagram — de verdad'].map((item, idx) => <li key={idx} className="flex items-start gap-3 text-gray-300 font-light" data-id="element-158">
                    <CheckCircle2 className="text-luxury-emerald w-5 h-5 shrink-0 mt-0.5" data-id="element-159" />
                    <span data-id="element-160">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            {/* Not For You */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="bg-gradient-to-b from-luxury-dark to-luxury-black p-10 rounded-sm border border-red-900/20 opacity-80" data-id="element-161">
              <h3 className="text-2xl font-serif text-white mb-8 flex items-center gap-3" data-id="element-162">
                <XCircle className="text-red-500/70" data-id="element-163" />
                Esto NO Es Para Ti Si...
              </h3>
              <ul className="space-y-6 mb-8" data-id="element-164">
                {['Quieres la opción más barata sin importar la calidad', 'No te importa cómo lucen tus uñas', 'Quieres entrar y salir en 20 minutos sin conversación'].map((item, idx) => <li key={idx} className="flex items-start gap-3 text-gray-400 font-light" data-id="element-165">
                    <XCircle className="text-red-500/50 w-5 h-5 shrink-0 mt-0.5" data-id="element-166" />
                    <span data-id="element-167">{item}</span>
                  </li>)}
              </ul>
              <div className="p-4 bg-white/5 rounded-sm border-l-2 border-gray-500 text-sm text-gray-400 italic" data-id="element-168">
                Las clientas de Paola vienen por una experiencia. Si quieres
                algo rápido y olvidable, hay muchos otros lugares para eso.
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROMISE */}
      <section className="py-12 md:py-24 bg-luxury-emeraldDark relative overflow-hidden" data-id="element-169">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10" data-id="element-170"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10" data-id="element-171">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-172">
            <motion.h2 variants={fadeIn} className="text-3xl md:text-5xl font-serif mb-8 text-white" data-id="element-173">
              La Promesa de Paola
            </motion.h2>
            <motion.p variants={fadeIn} className="text-xl md:text-2xl text-luxury-goldLight font-serif italic mb-10" data-id="element-174">
              "Cada set que Paola crea es tratado como una obra de arte con tu
              nombre en él."
            </motion.p>
            <motion.div variants={fadeIn} className="space-y-6 text-gray-200 text-lg font-light" data-id="element-175">
              <p data-id="element-176">
                No se apura. No corta esquinas. Y no te deja ir hasta que estés
                enamorada de tus uñas.
              </p>
              <p className="font-medium text-white" data-id="element-177">
                Eso no es una línea de marketing.
              </p>
              <p data-id="element-178">Es su reputación. Y en Corona, NY — todo el mundo lo sabe.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="reserva" className="py-12 md:py-24 bg-luxury-black relative" data-id="element-179">
        <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-180">
          <div className="bg-gradient-to-br from-luxury-dark to-luxury-charcoal p-8 md:p-16 rounded-sm border border-luxury-gold/20 shadow-[0_0_50px_rgba(201,168,76,0.05)] text-center" data-id="element-181">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-182">
              <motion.h2 variants={fadeIn} className="text-3xl md:text-5xl font-serif mb-6 text-white" data-id="element-183">
                No Dejes Pasar Otra Semana Con Uñas De Las Que No Estés
                Orgullosa
              </motion.h2>
              <motion.p variants={fadeIn} className="text-gray-300 text-lg mb-12 max-w-2xl mx-auto font-light" data-id="element-184">
                Trabajas demasiado duro. Mereces demasiado. Y tus manos son
                vistas todos los días. Haz que valgan la pena mirar.
              </motion.p>

              <motion.div variants={fadeIn} className="grid md:grid-cols-2 gap-8 mb-12 text-left" data-id="element-185">
                <div className="bg-luxury-black p-6 rounded-sm border border-white/5 flex items-start gap-4" data-id="element-186">
                  <Phone className="text-luxury-emerald w-6 h-6 shrink-0 mt-1" data-id="element-187" />
                  <div data-id="element-188">
                    <h4 className="text-white font-medium mb-1" data-id="element-189">
                      Llama o Escríbele
                    </h4>
                    <a href="tel:347-842-7103" className="text-2xl font-serif text-luxury-gold hover:text-luxury-goldLight transition-colors" data-id="element-190">
                      347-842-7103
                    </a>
                  </div>
                </div>
                <div className="bg-luxury-black p-6 rounded-sm border border-white/5 flex items-start gap-4" data-id="element-191">
                  <Instagram className="text-luxury-emerald w-6 h-6 shrink-0 mt-1" data-id="element-192" />
                  <div data-id="element-193">
                    <h4 className="text-white font-medium mb-1" data-id="element-194">Envía un DM</h4>
                    <a href="https://instagram.com/paotaborda_nails" target="_blank" rel="noopener noreferrer" className="text-xl text-gray-300 hover:text-white transition-colors" data-id="element-195">
                      @paotaborda_nails
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Google Maps Embed */}
              <motion.div variants={fadeIn} className="mb-12 w-full h-64 md:h-80 rounded-sm overflow-hidden border border-white/10" data-id="element-196">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1m3!1d3023.238612196027!2d-73.8631556!3d40.7567844!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25fdf2f1e63a3%3A0x89f41a87c1e5f8f8!2s107-05%20Northern%20Blvd%2C%20Corona%2C%20NY%2011368!5e0!3m2!1sen!2sus!4v1713400000000!5m2!1sen!2sus" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Paola Borda Nails Location" data-id="element-197"></iframe>
              </motion.div>

              <motion.div variants={fadeIn} className="bg-red-950/30 border border-red-500/20 p-6 rounded-sm text-left flex gap-4 items-start" data-id="element-198">
                <div className="text-red-400 text-2xl" data-id="element-199">⚠️</div>
                <div data-id="element-200">
                  <h4 className="text-white font-medium mb-2" data-id="element-201">
                    AVISO IMPORTANTE:
                  </h4>
                  <p className="text-gray-300 text-sm font-light" data-id="element-202">
                    El horario de Paola se llena rápido — especialmente los
                    viernes y sábados. No esperes hasta la semana de tu evento.
                    Escríbele ahora para asegurar tu lugar.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-luxury-black border-t border-white/10 py-12 md:py-16" data-id="element-203">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-204">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-205">
            <div className="flex-shrink-0" data-id="element-206">
              <img src="https://cdn.magicpatterns.com/uploads/reWmgqgz4SXDRvm7SyGVzz/logoPweb1.png" alt="Paola Borda Nails Logo" className="h-16 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity" data-id="element-207" />
            </div>

            <div className="flex flex-col md:flex-row gap-8 text-sm text-gray-400 font-light items-center" data-id="element-208">
              <div className="flex items-center gap-2" data-id="element-209">
                <MapPin className="w-4 h-4 text-luxury-emerald" data-id="element-210" />
                <span data-id="element-211">107-05 Northern Blvd, Corona, NY 11368</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-212">
                <Clock className="w-4 h-4 text-luxury-emerald" data-id="element-213" />
                <span data-id="element-214">Lun - Sáb | 10:00 AM – 8:00 PM</span>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500 font-light" data-id="element-215">
            <p data-id="element-216">
              &copy; {new Date().getFullYear()} Paola Borda Nails. Todos los
              derechos reservados.
            </p>
            <div className="flex gap-4" data-id="element-217">
              <button onClick={() => setIsPrivacyOpen(true)} className="hover:text-luxury-gold transition-colors" data-id="element-218">
                Política de Privacidad
              </button>
              <button onClick={() => setIsTermsOpen(true)} className="hover:text-luxury-gold transition-colors" data-id="element-219">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a href="https://wa.me/16468066813" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white pl-5 pr-6 py-3 rounded-full shadow-lg hover:scale-105 transition-transform duration-300 flex items-center gap-2" aria-label="Contactar por WhatsApp" data-id="element-220">
        <MessageCircle size={22} data-id="element-221" />
        <span className="text-sm font-medium" data-id="element-222">Contáctanos</span>
      </a>

      {/* Floating Social Proof Notification */}
      <AnimatePresence data-id="element-223">
        {showNotification && <motion.div initial={{
        opacity: 0,
        x: -100
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -100
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="fixed bottom-5 left-5 z-[9999] w-[300px] max-w-[85vw] bg-[#1A1A1A] text-gray-200 rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.5)] border-l-4 border-luxury-gold overflow-hidden" data-id="element-224">
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1" data-id="element-225">
              <div className="flex items-center gap-1.5" data-id="element-226">
                <span className="relative flex h-2 w-2" data-id="element-227">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-228"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" data-id="element-229"></span>
                </span>
                <span className="text-[10px] uppercase tracking-[1px] text-gray-500" data-id="element-230">
                  Reserva reciente verificada
                </span>
              </div>
              <button onClick={dismissNotification} className="text-gray-500 hover:text-gray-300 transition-colors" aria-label="Cerrar notificación" data-id="element-231">
                <X size={14} data-id="element-232" />
              </button>
            </div>

            {/* Content */}
            <div className="flex items-center gap-3 px-4 py-3" data-id="element-233">
              <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-luxury-gold/30" data-id="element-234">
                <img src="https://cdn.magicpatterns.com/uploads/9dZUkvq2HhpaCnsNJ5BxLa/logoPweb1.png" alt="PT Nails" className="w-full h-full object-cover" data-id="element-235" />
              </div>
              <div className="min-w-0" data-id="element-236">
                <p className="text-[13px] font-bold text-luxury-gold truncate" data-id="element-237">
                  {socialProofData[notificationIndex].name} de{' '}
                  {socialProofData[notificationIndex].area}
                </p>
                <p className="text-[12px] text-gray-300" data-id="element-238">
                  reservó una cita de{' '}
                  {socialProofData[notificationIndex].service}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-239">
                  hace {randomMinutes} minutos ⭐
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Privacy Policy Modal */}
      <AnimatePresence data-id="element-240">
        {isPrivacyOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-241">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-luxury-dark border border-white/10 rounded-sm p-6 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto" data-id="element-242">
              <div className="flex justify-between items-center mb-6" data-id="element-243">
                <h2 className="text-2xl font-serif text-white" data-id="element-244">
                  Política de Privacidad
                </h2>
                <button onClick={() => setIsPrivacyOpen(false)} className="text-gray-400 hover:text-white" data-id="element-245">
                  <X size={24} data-id="element-246" />
                </button>
              </div>
              <div className="space-y-4 text-gray-300 font-light text-sm" data-id="element-247">
                <p data-id="element-248">
                  En Paola Borda Nails, valoramos y respetamos su privacidad.
                  Esta política describe cómo recopilamos, usamos y protegemos
                  su información personal.
                </p>
                <h3 className="text-white font-medium text-base mt-4" data-id="element-249">
                  1. Información que recopilamos
                </h3>
                <p data-id="element-250">
                  Recopilamos información básica de contacto (nombre, número de
                  teléfono, correo electrónico) únicamente con el propósito de
                  agendar y confirmar sus citas.
                </p>
                <h3 className="text-white font-medium text-base mt-4" data-id="element-251">
                  2. Uso de la información
                </h3>
                <p data-id="element-252">
                  Su información se utiliza exclusivamente para gestionar sus
                  reservas, enviarle recordatorios de citas y comunicarnos sobre
                  nuestros servicios.
                </p>
                <h3 className="text-white font-medium text-base mt-4" data-id="element-253">
                  3. Protección de datos
                </h3>
                <p data-id="element-254">
                  Mantenemos medidas de seguridad para proteger su información
                  personal contra acceso no autorizado. No vendemos ni
                  compartimos sus datos con terceros.
                </p>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Terms Modal */}
      <AnimatePresence data-id="element-255">
        {isTermsOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-256">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-luxury-dark border border-white/10 rounded-sm p-6 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto" data-id="element-257">
              <div className="flex justify-between items-center mb-6" data-id="element-258">
                <h2 className="text-2xl font-serif text-white" data-id="element-259">
                  Términos y Condiciones
                </h2>
                <button onClick={() => setIsTermsOpen(false)} className="text-gray-400 hover:text-white" data-id="element-260">
                  <X size={24} data-id="element-261" />
                </button>
              </div>
              <div className="space-y-4 text-gray-300 font-light text-sm" data-id="element-262">
                <h3 className="text-white font-medium text-base" data-id="element-263">
                  1. Política de Citas y Cancelaciones
                </h3>
                <p data-id="element-264">
                  Solicitamos amablemente un aviso mínimo de 24 horas para
                  cancelaciones o reprogramaciones. Las cancelaciones tardías
                  pueden estar sujetas a un cargo.
                </p>
                <h3 className="text-white font-medium text-base mt-4" data-id="element-265">
                  2. Llegadas Tardías
                </h3>
                <p data-id="element-266">
                  Si llega más de 15 minutos tarde a su cita, es posible que
                  tengamos que reprogramar o acortar su servicio para no afectar
                  a los siguientes clientes.
                </p>
                <h3 className="text-white font-medium text-base mt-4" data-id="element-267">
                  3. Garantía de Servicio
                </h3>
                <p data-id="element-268">
                  Nos enorgullecemos de nuestro trabajo. Si experimenta algún
                  problema con sus uñas dentro de los primeros 3 días debido a
                  la aplicación, lo repararemos sin costo adicional.
                </p>
                <h3 className="text-white font-medium text-base mt-4" data-id="element-269">
                  4. Derecho de Admisión
                </h3>
                <p data-id="element-270">
                  Nos reservamos el derecho de negar el servicio a cualquier
                  persona que muestre un comportamiento inapropiado o
                  condiciones de salud en las uñas que requieran atención
                  médica.
                </p>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}